import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useNotificationSound } from '../hooks/useNotificationSound';

function Sidebar({ industry = 'beauty_salon', industryModules = {} }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1');
  const [moreOpen, setMoreOpen] = useState(false);
  const { soundEnabled, toggleSound } = useNotificationSound();
  const [unreadCount, setUnreadCount] = useState(0);
  const [preConsultCount, setPreConsultCount] = useState(0);

  useEffect(() => {
    // Tek endpoint - hem Mesajlar hem Ön Görüşme rozeti buradan besleniyor.
    // İkisi de SADECE BUGÜN gelenleri sayar (bkz. server/routes/dashboard.js
    // /nav-badges) - günler boyu biriken eski backlog rozeti anlamsızlaştırmasın diye.
    const fetchNavBadges = async () => {
      try {
        const token = localStorage.getItem('crm_token');
        if (!token) return;
        const res = await fetch('/api/dashboard/nav-badges', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.messages || 0);
          setPreConsultCount(data.pre_consultations_today || 0);
        }
      } catch (e) { console.error(e); }
    };

    fetchNavBadges();
    const interval = setInterval(fetchNavBadges, 15000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', isCollapsed);
    localStorage.setItem('sidebar_collapsed', isCollapsed ? '1' : '0');
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [isCollapsed]);

  const isModuleEnabled = (module) => {
    if (industryModules[module] !== true) return false;
    if (['owner', 'manager'].includes(user?.role)) return true;
    const perms = user?.permissions;
    if (!perms || !Array.isArray(perms) || perms.length === 0) return true;
    return perms.includes(module);
  };

  const baseNavItems = [
    { path: '/', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/egitim', label: 'Eğitim', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25' },
    { path: '/calendar', label: 'Randevular', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', module: 'calendar' },
    { path: '/pre-consultations', label: 'Ön Görüşme', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', module: 'preConsultations' },
    { path: '/customers', label: 'Müşteriler', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', module: 'customers' },
    { path: '/kanban', label: 'Lead Pipeline', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2', module: 'kanban' },
    { path: '/callbacks', label: 'Aramalar', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', module: 'callbacks' },
    { path: '/messages', label: 'Mesajlar', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z', module: 'messages' },
    { path: '/campaigns', label: 'Kampanyalar', icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z', module: 'campaigns' },
    { path: '/reports', label: 'Raporlar', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', module: 'reports' },
    { path: '/calls', label: 'Çağrılar', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', module: 'calls' },
    { path: '/announcements', label: 'Güncellemeler', icon: 'M11 5h2a2 2 0 012 2v3l3 3v2h-6m-4 0a2 2 0 104 0m-4 0H5v-2l3-3V7a2 2 0 012-2h0', module: 'announcements' },
    { path: '/notifications', label: 'Bildirimler', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', module: 'notifications' },
    { path: '/finance', label: 'Finans', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', module: 'finance' },
    { path: '/automations', label: 'İş Akışları', icon: 'M13 10V3L4 14h7v7l9-11h-7z', module: 'automations' },
    { path: '/pos', label: 'Kasa / POS', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z', module: 'pos' },
    { path: '/products', label: 'Stok', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', module: 'products' },
    { path: '/meta-ads', label: 'Meta Ads', icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z', module: 'metaAds' },
    { path: '/integrations', label: 'Entegrasyonlar', icon: 'M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5', module: 'integrations' },
    { path: '/opportunities', label: 'Gelir Fırsatları', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6', module: 'opportunities' },
    { path: '/settings', label: 'Ayarlar', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', module: 'settings' },
  ];

  const beautyNavItems = [
    { path: '/staff', label: 'Personel', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', module: 'staff' },
    { path: '/payroll', label: 'Bordro & Maaş', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', module: 'payroll' },
    { path: '/services', label: 'Hizmetler', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', module: 'services' },
  ];

  const bridalNavItems = [
    { path: '/measurements', label: 'Ölçü Kartı', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', module: 'measurements' },
    { path: '/fitting', label: 'Prova', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', module: 'fitting' },
    { path: '/rental', label: 'Kiralama', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', module: 'rental' },
  ];

  const dusakabinNavItems = [
    { path: '/surveys', label: 'Keşif / Ölçü', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', module: 'surveys' },
    { path: '/quotes', label: 'Teklifler', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', module: 'quotes' },
    { path: '/production', label: 'Üretim', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', module: 'production' },
  ];

  const weddingNavItems = [
    { path: '/reservations', label: 'Rezervasyon', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', module: 'reservations' },
    { path: '/packages', label: 'Paketler', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', module: 'packages' },
    { path: '/event-checklist', label: 'Checklist', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', module: 'eventChecklist' },
    { path: '/wedding-vendors', label: 'Tedarikçiler', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', module: 'weddingVendors' },
  ];

  const healthTourismNavItems = [
    { path: '/patient-tracking', label: 'Hasta Takibi', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', module: 'patientTracking' },
  ];

  const hotelNavItems = [
    { path: '/hotel-reservations', label: 'Rezervasyon', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', module: 'hotelReservations' },
  ];

  const dentalNavItems = [
    { path: '/dental-treatments', label: 'Tedavi', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z', module: 'dentalTreatments' },
    { path: '/auto-reminders', label: 'Hatırlatma', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', module: 'autoReminders' },
  ];

  const clinicNavItems = [
    { path: '/departments', label: 'Departmanlar', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', module: 'departments' },
    { path: '/patient-satisfaction', label: 'Memnuniyet', icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', module: 'patientSatisfaction' },
    { path: '/auto-reminders', label: 'Hatırlatma', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', module: 'autoReminders' },
  ];

  const ecommerceNavItems = [
    { path: '/ecommerce-orders', label: 'Siparişler', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z', module: 'ecommerceOrders' },
    { path: '/return-management', label: 'İadeler', icon: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6', module: 'returnManagement' },
  ];

  const getVisibleNavItems = () => {
    const rawItems = [
      ...baseNavItems,
      ...beautyNavItems,
      ...bridalNavItems,
      ...dusakabinNavItems,
      ...weddingNavItems,
      ...healthTourismNavItems,
      ...hotelNavItems,
      ...dentalNavItems,
      ...clinicNavItems,
      ...ecommerceNavItems
    ];
    const uniqueItemsMap = new Map();
    rawItems.forEach(item => {
      if (!uniqueItemsMap.has(item.path)) uniqueItemsMap.set(item.path, item);
    });
    return Array.from(uniqueItemsMap.values()).filter(item => {
      if (!item.module) return true;
      return isModuleEnabled(item.module);
    });
  };

  const navItems = getVisibleNavItems();
  const siteSettings = useSiteSettings();
  // Logo/isim alan adına göre markadan gelir (bkz. server/utils/brands.js).
  const siteName = siteSettings.site_name || 'CRM';
  const siteLogoUrl = siteSettings.site_logo_url || '';
  // Logo dosyası sunucuda yoksa (ör. silinmiş) kırık görsel yerine varsayılan ikon gösterilir.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => { setLogoFailed(false); }, [siteLogoUrl]);

  const getRoleBadge = (role) => {
    const badges = {
      owner: { bg: '#00f2fe', label: 'Sahip' },
      manager: { bg: '#8b5cf6', label: 'Yönetici' },
      senior_staff: { bg: '#f43f5e', label: 'Kıdemli' },
      staff: { bg: '#10b981', label: 'Personel' },
      intern: { bg: '#fbbf24', label: 'Stajyer' },
    };
    return badges[role] || badges.staff;
  };

  const roleInfo = getRoleBadge(user?.role);

  // Mobil alt gezinme çubuğu ve "Menü" çekmecesi masaüstü sidebar'daki rozeti
  // paylaşmıyor (ayrı render bloğu) - aynı bugünkü sayıyı orada da göstermek için.
  const getBadgeCount = (path) => {
    if (path === '/messages') return unreadCount;
    if (path === '/pre-consultations') return preConsultCount;
    return 0;
  };

  // Mobil alt çubuk: eskiden menünün ilk 4 öğesiydi, bu yüzden Eğitim 2. sıraya
  // düşüyordu. Artık sabit öncelik sırası; bu şube için kapalı olan bir modül
  // varsa boşluk menünün sıradaki öğesiyle doldurulur. Kalan her şey (Eğitim
  // dahil) "Menü" çekmecesinde. Masaüstü kenar menüsü değişmez.
  const MOBILE_BOTTOM_PRIORITY = ['/', '/messages', '/calendar', '/pre-consultations'];
  const prioritized = MOBILE_BOTTOM_PRIORITY
    .map(p => navItems.find(item => item.path === p))
    .filter(Boolean);
  const bottomItems = [
    ...prioritized,
    ...navItems.filter(item => !prioritized.includes(item) && item.path !== '/egitim')
  ].slice(0, 4);
  const moreItems = navItems.filter(item => !bottomItems.includes(item));

  return (
    <>
      <aside className={`sidebar${isCollapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">
              {siteLogoUrl && !logoFailed ? (
                <img src={siteLogoUrl} alt={siteName} onError={() => setLogoFailed(true)} style={{ width: '100%', objectFit: 'contain' }} />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
            </div>
          </div>
          <button className="sidebar-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isCollapsed ? (
                <path d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              ) : (
                <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              )}
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              end={item.path === '/'}
              title={isCollapsed ? item.label : undefined}
            >
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
              </svg>
              <span className="nav-label">{item.label}</span>
              {item.path === '/messages' && unreadCount > 0 && (
                <span className="sidebar-badge" style={{
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '10px',
                  padding: '2px 6px',
                  marginLeft: 'auto'
                }}>{unreadCount}</span>
              )}
              {item.path === '/pre-consultations' && preConsultCount > 0 && (
                <span className="sidebar-badge" style={{
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '10px',
                  padding: '2px 6px',
                  marginLeft: 'auto'
                }}>{preConsultCount}</span>
              )}
              {location.pathname === item.path && <div className="nav-active-indicator" />}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-card">
            <div className="user-avatar" style={{ background: roleInfo.bg }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.first_name} {user?.last_name}</div>
              <div className="user-role" style={{ background: roleInfo.bg + '20', color: roleInfo.bg }}>
                {roleInfo.label}
              </div>
            </div>
          </div>
          <button
            className="logout-btn"
            onClick={toggleSound}
            title={soundEnabled ? 'Sesi Kapat' : 'Sesi Aç'}
            style={{ marginRight: '4px', color: soundEnabled ? '#10b981' : 'rgba(255,255,255,0.3)' }}
          >
            {soundEnabled ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-4-4m4 4l4-4M9.172 9.172a4 4 0 000 5.656" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5z" />
                <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
          <button className="logout-btn" onClick={logout} title="Çıkış">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </aside>

      <nav className="mobile-bottom-nav">
        {bottomItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const badgeCount = getBadgeCount(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`mob-tab${isActive ? ' mob-tab-active' : ''}`}
              end={item.path === '/'}
            >
              <span className="mob-tab-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
                {badgeCount > 0 && <span className="mob-tab-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>}
              </span>
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        {moreItems.length > 0 && (
          <button
            className={`mob-tab${moreOpen ? ' mob-tab-active' : ''}`}
            onClick={() => setMoreOpen(!moreOpen)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span>Menü</span>
          </button>
        )}
      </nav>

      {moreOpen && (
        <div className="mob-more-overlay" onClick={() => setMoreOpen(false)}>
          <div className="mob-more-drawer" onClick={e => e.stopPropagation()}>
            <div className="mob-more-header">
              <div className="user-avatar" style={{ background: roleInfo.bg, width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, color: 'white' }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{user?.first_name} {user?.last_name}</div>
                <div style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: roleInfo.bg + '25', color: roleInfo.bg, fontWeight: 600, display: 'inline-block', marginTop: 3 }}>
                  {roleInfo.label}
                </div>
              </div>
            </div>

            <div className="mob-more-grid">
              {moreItems.map(item => {
                const isActive = location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                const badgeCount = getBadgeCount(item.path);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={`mob-more-item${isActive ? ' mob-more-item-active' : ''}`}
                  >
                    <span className="mob-tab-icon-wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                      </svg>
                      {badgeCount > 0 && <span className="mob-tab-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>}
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>

            <button className="mob-logout-btn" onClick={logout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Çıkış Yap
            </button>
          </div>
        </div>
      )}

      <style>{`
        .sidebar {
          width: var(--sidebar-width);
          height: 100vh;
          position: fixed;
          left: 0;
          top: 0;
          background: rgba(3, 4, 7, 0.4);
          border-right: 1px solid var(--border);
          backdrop-filter: blur(20px);
          display: flex;
          flex-direction: column;
          z-index: 50;
          transition: var(--transition);
        }

        .sidebar::before {
          content: '';
          position: absolute;
          top: 20%;
          left: 0;
          width: 2px;
          height: 60%;
          background: linear-gradient(180deg, transparent, var(--primary), transparent);
          border-radius: 2px;
        }

        .sidebar-header {
          height: 80px;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid var(--border);
        }

        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-icon {
          width: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .logo-icon svg { width: 22px; height: 22px; }

        .logo-text {
          font-size: 18px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.5px;
          white-space: nowrap;
        }

        .sidebar-toggle {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          cursor: pointer;
          transition: var(--transition-fast);
        }
        .sidebar-toggle:hover {
          background: rgba(255, 255, 255, 0.06);
          color: #ffffff;
        }
        .sidebar-toggle svg { width: 16px; height: 16px; }

        .sidebar-nav {
          flex: 1;
          padding: 16px 12px;
          overflow-y: auto;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          color: var(--text-muted);
          text-decoration: none;
          transition: var(--transition-fast);
          position: relative;
          overflow: hidden;
        }

        .nav-item:hover {
          background: rgba(var(--primary-rgb), 0.05);
          color: var(--text);
        }

        .nav-item.active {
          background: rgba(var(--primary-rgb), 0.1);
          color: var(--primary);
          font-weight: 600;
        }

        .nav-icon { width: 20px; height: 20px; flex-shrink: 0; }
        .nav-label { font-size: 13.5px; font-weight: 500; }

        .nav-active-indicator {
          position: absolute;
          right: 14px;
          width: 6px;
          height: 6px;
          background: var(--primary);
          border-radius: 50%;
          box-shadow: 0 0 8px var(--primary);
          animation: glowPulse 2s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; box-shadow: 0 0 6px var(--primary); }
          50%       { opacity: 1;   box-shadow: 0 0 12px var(--primary); }
        }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .user-card { display: flex; align-items: center; gap: 12px; }

        .user-avatar {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
          color: white;
          box-shadow: 0 0 12px rgba(0,0,0,0.4);
        }

        .user-info { display: flex; flex-direction: column; }
        .user-name { color: #FFFFFF; font-weight: 600; font-size: 13px; }
        .user-role { font-size: 10px; padding: 2px 8px; border-radius: 999px; font-weight: 600; margin-top: 2px; }

        .logout-btn {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.15);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .logout-btn svg { width: 17px; height: 17px; color: rgba(239, 68, 68, 0.7); }
        .logout-btn:hover { background: rgba(239, 68, 68, 0.18); border-color: rgba(239, 68, 68, 0.4); }
        .logout-btn:hover svg { color: #EF4444; }

        .mobile-bottom-nav { display: none; }

        @media (min-width: 769px) {
          .sidebar.sidebar-collapsed {
            width: 84px;
          }
          .sidebar.sidebar-collapsed .sidebar-header {
            padding: 0 16px;
            justify-content: center;
          }
          .sidebar.sidebar-collapsed .sidebar-logo {
            display: none;
          }
          .sidebar.sidebar-collapsed .nav-label,
          .sidebar.sidebar-collapsed .user-info {
            display: none;
          }
          .sidebar.sidebar-collapsed .nav-item {
            justify-content: center;
            padding: 12px;
          }
          .sidebar.sidebar-collapsed .nav-active-indicator {
            display: none;
          }
          .sidebar.sidebar-collapsed .sidebar-footer {
            flex-direction: column;
            gap: 8px;
          }
          .sidebar.sidebar-collapsed .user-card {
            gap: 0;
          }

          body.sidebar-collapsed .main-content {
            margin-left: 84px;
          }
        }

        @media (max-width: 768px) {
          .sidebar { display: none; }

          .mobile-bottom-nav {
            display: flex;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 200;
            background: rgba(3, 4, 7, 0.97);
            border-top: 1px solid var(--border);
            backdrop-filter: blur(24px) saturate(180%);
            padding: 0;
            padding-bottom: env(safe-area-inset-bottom, 0px);
            box-shadow: 0 -8px 32px rgba(0,0,0,0.6);
          }

          .mob-tab {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            padding: 10px 4px;
            color: rgba(255,255,255,0.35);
            text-decoration: none;
            background: none;
            border: none;
            cursor: pointer;
            font-family: inherit;
            transition: color 0.2s ease;
            min-width: 0;
          }

          .mob-tab svg {
            width: 22px;
            height: 22px;
            flex-shrink: 0;
            transition: all 0.2s ease;
          }

          .mob-tab-icon-wrap {
            position: relative;
            display: inline-flex;
          }

          .mob-tab-badge {
            position: absolute;
            top: -6px;
            right: -10px;
            background: #ef4444;
            color: #fff;
            font-size: 9px;
            font-weight: 700;
            border-radius: 8px;
            padding: 1px 4px;
            min-width: 14px;
            text-align: center;
            line-height: 1.4;
          }

          .mob-tab span {
            font-size: 9.5px;
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
          }

          .mob-tab-active {
            color: var(--primary);
          }

          .mob-tab-active svg {
            filter: drop-shadow(0 0 6px rgba(var(--primary-rgb), 0.7));
          }

          .mob-more-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.7);
            backdrop-filter: blur(6px);
            z-index: 300;
            display: flex;
            align-items: flex-end;
          }

          .mob-more-drawer {
            width: 100%;
            background: rgba(3, 4, 7, 0.98);
            border-top: 1px solid var(--border);
            border-radius: 24px 24px 0 0;
            padding: 20px 16px;
            padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
            animation: slideUpDrawer 0.28s cubic-bezier(0.16, 1, 0.3, 1);
          }

          @keyframes slideUpDrawer {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }

          .mob-more-header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding-bottom: 16px;
            border-bottom: 1px solid rgba(255,255,255,0.06);
            margin-bottom: 16px;
          }

          .mob-more-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            margin-bottom: 20px;
          }

          .mob-more-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            padding: 14px 8px;
            border-radius: 14px;
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.06);
            color: rgba(255,255,255,0.5);
            text-decoration: none;
            font-size: 10px;
            font-weight: 500;
            text-align: center;
            transition: all 0.2s ease;
          }

          .mob-more-item svg {
            width: 22px;
            height: 22px;
          }

          .mob-more-item:hover, .mob-more-item-active {
            background: rgba(var(--primary-rgb), 0.12);
            border-color: rgba(var(--primary-rgb), 0.25);
            color: var(--primary);
          }

          .mob-logout-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 14px;
            border-radius: 14px;
            background: rgba(239, 68, 68, 0.08);
            border: 1px solid rgba(239, 68, 68, 0.2);
            color: rgba(239, 68, 68, 0.8);
            font-size: 14px;
            font-weight: 600;
            font-family: inherit;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .mob-logout-btn svg { width: 18px; height: 18px; }
          .mob-logout-btn:hover { background: rgba(239, 68, 68, 0.15); color: #EF4444; }
        }
      `}</style>
    </>
  );
}

export default Sidebar;
